import type {
  FetchUserDataFn,
  GetCurrentMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetUserDataFn,
} from '@/features/iam'
import type { FetchDocumentTypesFn } from '@/features/documents'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import type { NavigateFn } from '@/RootStore'
import { links } from '@/utils/link'
import { type Job } from '@/features/jobs/Job'
import { jobClosingFlow } from '@/features/jobs/details/job-closing-flow'
import { type JobStore } from '@/features/jobs/JobStore'
import { action, observable, computed } from 'mobx'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type Observable, Subject } from 'rxjs'
import { filter, tap } from 'rxjs/operators'
import { Id } from '@/infra/id'
import { isRefreshDesign } from '@/infra/design'
import { CloseJobAsCollaboratorDialogViewModel } from '@/features/jobs/CloseJobAsCollaboratorDialog/CloseJobAsCollaboratorDialogViewModel'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { type JobListViewModel } from '@/features/jobs/details/components/JobList/JobListViewModel'

export type IsFirmAdminInWorkspaceFn = (workspaceId: Id) => boolean

/**
 * Responsible for managing the current Job view model as well
 * as the job list.
 */
export class JobRootViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The current Close Job dialog, if any.
   */
  @observable
  accessor closeJobDialog: CloseJobAsCollaboratorDialogViewModel | null = null

  /**
   * Subject for requesting to show the Close Job dialog.
   */
  readonly requestClose$ = new Subject<Job>()

  constructor(
    private readonly _navigate: NavigateFn,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _jobStore: JobStore,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _getUserData: GetUserDataFn,
    private readonly _fetchUserData: FetchUserDataFn,
    private readonly _fetchDocumentTypes: FetchDocumentTypesFn,
    private readonly _narrowViewport$: Observable<boolean>,
    public readonly jobListVm: JobListViewModel,
    private readonly _jobDetailsVms: ReadonlySet<JobDetailsViewModel>,
  ) {
    this._lifecycle.manage(this.jobListVm)
    this._lifecycle.manage(
      jobClosingFlow(this.requestClose$).pipe(tap(this._showCloseJobDialog)),
    )
    this._lifecycle.manage(
      this._narrowViewport$.pipe(
        filter(
          (narrow) =>
            narrow && !isRefreshDesign && this.jobListVm.panel.showing,
        ),
        tap(() => this.jobListVm.toggleVisibility()),
      ),
    )
  }

  /**
   * The job being viewed, if any.
   */
  @computed
  get currentJob() {
    return this._current?.job ?? null
  }

  /**
   * The conversation of the job being viewed, if any.
   */
  @computed
  get activeConversation() {
    return this._lifecycle.active
      ? (this._current?.job.conversation ?? null)
      : null
  }

  /**
   * Whether the loaded job list has a job before the one being viewed.
   */
  @computed
  get hasPreviousJob() {
    return this.jobListVm.adjacentJobId(-1) !== null
  }

  /**
   * Whether the loaded job list has a job after the one being viewed.
   */
  @computed
  get hasNextJob() {
    return this.jobListVm.adjacentJobId(1) !== null
  }

  /**
   * Whether the user can edit draft jobs.
   */
  @computed
  get canEditDraftJobAsAdmin() {
    return this._getCurrentUserWorkspaceMemberships().some(
      (x) => x.member.permissions.adminJobCreate,
    )
  }

  /**
   * Whether the user can update a job as an admin.
   */
  @computed
  get canUpdateJobAsAdmin() {
    return this._getCurrentUserWorkspaceMemberships().some(
      (m) => m.member.permissions.adminJobUpdate,
    )
  }

  /**
   * Determinate if the current member is a firm admin in the workspace stored in the session.
   */
  @computed
  get isFirmAdmin() {
    // Since we don't pass a workspace id, it will take the one in the session.
    return this.isFirmAdminInWorkspace()
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    const { signal } = this._lifecycle
    void this._createAlertRetryWrapper(
      'Loading the document types failed',
      signal,
    )(() => this._fetchDocumentTypes()).catch(() => undefined)
    void this._createAlertRetryWrapper(
      'Loading your user data failed',
      signal,
    )(() =>
      this._fetchUserData(
        Id.assert(this._getCurrentMember().user.publicId),
        undefined,
        signal,
      ),
    ).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Opens the job before the one being viewed in the loaded job list.
   */
  @action.bound
  viewPreviousJob() {
    this._viewAdjacentJob(-1)
  }

  /**
   * Opens the job after the one being viewed in the loaded job list.
   */
  @action.bound
  viewNextJob() {
    this._viewAdjacentJob(1)
  }

  /**
   * Determinate if the current member is a firm admin in the given workspace
   * by checking the user email, user type and workspace type.
   *
   * @param workspaceId
   * @returns
   */
  isFirmAdminInWorkspace(workspaceId?: Id) {
    const member = this._getCurrentMember(workspaceId)
    return (
      this._getUserData(member.user.publicId)?.userDataType === 'User' &&
      member.workspace.classification === 'B2B' &&
      !member.user.isStaff
    )
  }

  /**
   * The details view model of the job being viewed, which is the active one.
   */
  @computed
  private get _current() {
    for (const vm of this._jobDetailsVms) {
      if (vm.isActive) {
        return vm
      }
    }
    return null
  }

  private _viewAdjacentJob(offset: -1 | 1) {
    const jobId = this.jobListVm.adjacentJobId(offset)
    if (jobId) {
      this._navigate(links.job(jobId))
    }
  }

  @action.bound
  private _showCloseJobDialog(job: Job) {
    this.closeJobDialog = new CloseJobAsCollaboratorDialogViewModel(
      job,
      this._jobStore,
    )
    this.closeJobDialog.modal.show()
  }
}
